// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from "./axios";
import { toast } from "sonner";
import { showError, showSuccess } from "../actions/globalActions";
import getHeaders from "../utils/getHeaders"; // Ensure this utility is correctly imported

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const useLayoutStore = create(immer((set) => ({
  layouts: [],
  posts: [],

  // Methods to manipulate the state
  setLayouts: (layouts) => {
    set((state) => {
      state.layouts = layouts;
    });
  },

  setPosts: (posts) => {
    set((state) => {
      state.posts = posts;
    });
  },

  // Asynchronous actions
  fetchLayouts: async (leagueName = "") => {
    try {
      const { data } = await axios.get(`${baseUrl}/layouts/organizationName/${leagueName}`);
      set((state) => {
        state.layouts = data.result;
      });
    } catch (error) {
      console.error("Error in fetchLayouts:", error);
    }
  },

  fetchLayoutsByDivisionId: async (divisionId = "") => {
    try {
      const { data } = await axios.get(`${baseUrl}/layouts/division/${divisionId}`);
      set((state) => {
        const incoming = data.result || [];
        const incomingIds = new Set(incoming.map((l) => l._id));
        // Merge: drop any stale entries for this division, then add fresh.
        state.layouts = [
          ...state.layouts.filter((l) => !incomingIds.has(l._id)),
          ...incoming,
        ];
      });
    } catch (error) {
      console.error("Error in fetchLayoutsByDivisionId:", error);
    }
  },

  updateLayout: (getToken) => {
    return async (layout = {}, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.put(`${baseUrl}/layouts/d/${layout._id}`, layout, createAuthHeader(token));
        data?.error ? toast.error(data?.error) : toast.success(`Successfully Updated!`);  
        cb && cb(data?.result);
        set((state) => {
          const index = state.layouts.findIndex(l => l._id === layout._id);
          if (index !== -1) {
            state.layouts[index] = data.result;
          }
        });
        
      } catch (error) {
        showError("Error", "Update Error");
        console.error("Error in updateLayout:", error);
      }
    }
  },

  addLayout: (getToken) => {
    return async (layout = {}, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.post(`${baseUrl}/layouts/d`, layout, createAuthHeader(token));
        data?.error ? toast.error(data?.error) : toast.success(`Successfully Updated!`);  
        cb && cb(data?.result);
        set((state) => {
          state.layouts.push(data.result);
        });
        
      } catch (error) {
        console.error("Error in addLayout:", error);
      }
    }
  },

  // updateLayout minus the toasts — the canvas editor autosaves every ~1.5s
  // (chip feedback in its top bar). Failures REJECT so the caller can requeue
  // the dirty draft; the toasting action above stays for explicit saves.
  saveLayoutSilent: (getToken) => {
    return async (layout = {}) => {
      const token = await getToken();
      const { data } = await axios.put(`${baseUrl}/layouts/d/${layout._id}`, layout, createAuthHeader(token));
      if (data?.error) throw new Error(data.error);
      set((state) => {
        const index = state.layouts.findIndex((l) => l._id === layout._id);
        if (index !== -1 && data?.result) {
          state.layouts[index] = data.result;
        }
      });
      return data?.result;
    };
  },

  // Canvas create — quiet (selection is the feedback), REJECTS on error.
  // Callers must send `organization` (the API's requireBodyOrgMembership
  // guard 400s without it) and SHOULD send `active: true` — the schema
  // default is false, which hides a new header from the public nav.
  addLayoutSilent: (getToken) => {
    return async (layout = {}) => {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/layouts/d`, layout, createAuthHeader(token));
      if (data?.error) throw new Error(data.error);
      set((state) => {
        if (data?.result) state.layouts.push(data.result);
      });
      return data?.result;
    };
  },

  deleteLayout: (getToken) => {
    return async (layout = {}, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.delete(`${baseUrl}/layouts/d/${layout._id}`, createAuthHeader(token));
        data?.error ? toast.error(data?.error) : toast.success(`Successfully Deleted!`);  
        cb && cb(data?.result);
        set((state) => {
          state.layouts = state.layouts.filter(l => l._id !== layout._id);
        });
        
      } catch (error) {
        console.error("Error in deleteLayout:", error);
      }
    }
  },

  fetchPost: async (id, token) => {
    try {
      const headers = getHeaders(token);
      const post = (await axios.get(`${baseUrl}/posts/${id}`, { headers })).data;
      set((state) => {
        state.posts.push(post);
      });
    } catch (error) {
      console.error("Error in fetchPost:", error);
    }
  },

  savePost: async (post, token) => {
    try {
      let headers = getHeaders(token);
      let updatedPost;

      if (post.id) {
        updatedPost = (await axios.put(`${baseUrl}/posts/${post.id}`, post, { headers })).data;
      } else {
        updatedPost = (await axios.post(`${baseUrl}/posts`, post, { headers })).data;
      }

      set((state) => {
        const index = state.posts.findIndex(p => p.id === updatedPost.id);
        if (index !== -1) {
          state.posts[index] = updatedPost;
        } else {
          state.posts.push(updatedPost);
        }
      });
    } catch (error) {
      console.error("Error in savePost:", error);
    }
  },

})));

export default useLayoutStore;
